import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../../../../../services/axiosClient';
import swal from "sweetalert";
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Paragraph from '@editorjs/paragraph';
import Underline from '@editorjs/underline';

// Utility to get auth header
function getAuthHeader() {
  try {
    const str = localStorage.getItem('userDetails');
    if (!str) return {};
    const { token, tokenType } = JSON.parse(str) || {};
    if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
    return {};
  } catch (_) {
    return {};
  }
}

const ModernJobLists = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPosts, setSelectedPosts] = useState([]);
    const [expandedPost, setExpandedPost] = useState(null);
    const [expandedPostUser, setExpandedPostUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [filters, setFilters] = useState({
        search: '',
        country_code: 'SN',
        status: '',
        hasPromotion: '',
        hasValidPromotion: '',
        from_date: '',
        to_date: ''
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [formData, setFormData] = useState({});
    const companyEditorRef = useRef(null);
    const postEditorRef = useRef(null);
    const companyEditorInstance = useRef(null);
    const postEditorInstance = useRef(null);
    const [cities, setCities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [postTypes, setPostTypes] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingPostTypes, setLoadingPostTypes] = useState(false);

    // Récupérer les posts depuis l'API
    useEffect(() => {
        fetchPosts();
        fetchCities();
        fetchCategories();
        fetchPostTypes();
    }, [currentPage, itemsPerPage, filters]);

    // Fetch cities from API
    const fetchCities = async () => {
        setLoadingCities(true);
        try {
            const response = await axiosClient.get('/countries/SN/cities?perPage=100', {
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            });
            
            let cityList = response?.data?.result?.data || response?.data?.data || response?.data?.result || response?.data || [];
            if (!Array.isArray(cityList)) {
                cityList = [];
            }
            setCities(cityList);
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    };

    // Fetch categories from API
    const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await axiosClient.get('/categories', {
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            });
            
            let categoryList = response?.data?.result?.data || response?.data?.data || response?.data?.result || response?.data || [];
            if (!Array.isArray(categoryList)) {
                categoryList = [];
            }
            setCategories(categoryList);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    };

    // Fetch post types from API
    const fetchPostTypes = async () => {
        setLoadingPostTypes(true);
        try {
            const response = await axiosClient.get('/postTypes', {
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            });
            
            let postTypeList = response?.data?.result?.data || response?.data?.data || response?.data?.result || response?.data || [];
            if (!Array.isArray(postTypeList)) {
                postTypeList = [];
            }
            setPostTypes(postTypeList);
        } catch (error) {
            console.error('Error fetching post types:', error);
            setPostTypes([]);
        } finally {
            setLoadingPostTypes(false);
        }
    };

    const fetchPosts = async () => {
        try {
            setLoading(true);
            
            // Construire les paramètres pour l'API admin
            const params = {
                page: currentPage,
                perPage: itemsPerPage
            };
            
            // Ajouter les filtres s'ils sont renseignés
            if (filters.search) params.search = filters.search;
            if (filters.country_code) params.country_code = filters.country_code;
            if (filters.status) params.status = filters.status;
            if (filters.hasPromotion) params.hasPromotion = filters.hasPromotion;
            if (filters.hasValidPromotion) params.hasValidPromotion = filters.hasValidPromotion;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            
            const response = await axiosClient.get('/admin/posts', {
                params: params
            });

            const data = response.data;
            const postsData = data?.result?.data || data?.data || [];
            const pagination = data?.result?.meta || data?.meta || {};

            setPosts(postsData);
            setTotalPages(pagination.last_page || 1);
        } catch (error) {
            console.error('Error fetching posts:', error);
            swal('Erreur', 'Impossible de charger les annonces', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Sélectionner/désélectionner un post et afficher les détails
    const toggleSelectPost = async (postId) => {
        setSelectedPosts(prev => 
            prev.includes(postId) 
                ? prev.filter(id => id !== postId)
                : [...prev, postId]
        );
        
        // Toggle expanded view
        if (expandedPost === postId) {
            setExpandedPost(null);
            setExpandedPostUser(null);
        } else {
            setExpandedPost(postId);
            // Fetch user details
            const post = posts.find(p => p.id === postId);
            if (post && post.user_id) {
                await fetchUserDetails(post.user_id);
            }
        }
    };

    // Fetch user details
    const fetchUserDetails = async (userId) => {
        try {
            setLoadingUser(true);
            const config = {
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            };
            const response = await axiosClient.get(`/users/${userId}`, config);
            const userData = response?.data?.result || response?.data || {};
            setExpandedPostUser(userData);
        } catch (error) {
            console.error('Error fetching user:', error);
            setExpandedPostUser(null);
        } finally {
            setLoadingUser(false);
        }
    };

    // Fetch post details for editing
    const fetchPostDetails = async (postId) => {
        try {
            const config = {
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            };
            const response = await axiosClient.get(`/posts/${postId}`, config);
            const postData = response?.data?.result || response?.data || {};
            setEditingPost(postData);
            const newFormData = {
                title: postData.title || '',
                company_name: postData.company_name || '',
                company_description: postData.company_description || '',
                description: postData.description || '',
                city_id: postData.city_id || '',
                salary_min: postData.salary_min || '',
                salary_max: postData.salary_max || '',
                salary_type: postData.salary_type || '',
                application_url: postData.application_url || '',
                email: postData.email || '',
                phone: postData.phone || '',
                contact_name: postData.contact_name || '',
                tags: postData.tags || '',
                category_id: postData.category_id || '',
                post_type_id: postData.post_type_id || '',
                start_date: postData.start_date || '',
                end_date: postData.end_date || '',
                negotiable: postData.negotiable || 0,
                permanent: postData.permanent || 0
            };
            setFormData(newFormData);
            setShowEditModal(true);
        } catch (error) {
            console.error('Error fetching post details:', error);
            swal('Erreur', 'Impossible de charger les détails de l\'annonce', 'error');
        }
    };

    // Helper function to convert HTML to EditorJS blocks
    const htmlToEditorBlocks = (html) => {
        if (!html) return [];
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const blocks = [];
        
        tempDiv.childNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();
                const text = node.textContent || '';
                
                if (tagName === 'p') {
                    blocks.push({
                        type: 'paragraph',
                        data: { text: node.innerHTML }
                    });
                } else if (tagName === 'ul') {
                    const items = Array.from(node.querySelectorAll('li')).map(li => li.textContent);
                    blocks.push({
                        type: 'list',
                        data: { style: 'unordered', items }
                    });
                } else if (tagName === 'ol') {
                    const items = Array.from(node.querySelectorAll('li')).map(li => li.textContent);
                    blocks.push({
                        type: 'list',
                        data: { style: 'ordered', items }
                    });
                } else if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
                    blocks.push({
                        type: 'header',
                        data: { text, level: parseInt(tagName[1]) }
                    });
                } else {
                    blocks.push({
                        type: 'paragraph',
                        data: { text: node.innerHTML }
                    });
                }
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                blocks.push({
                    type: 'paragraph',
                    data: { text: node.textContent }
                });
            }
        });
        
        return blocks.length > 0 ? blocks : [{ type: 'paragraph', data: { text: '' } }];
    };

    // Initialize EditorJS instances when modal opens
    useEffect(() => {
        if (showEditModal && formData.company_description !== undefined) {
            setTimeout(() => {
                // Destroy existing instances
                if (companyEditorInstance.current) {
                    companyEditorInstance.current.destroy();
                    companyEditorInstance.current = null;
                }
                if (postEditorInstance.current) {
                    postEditorInstance.current.destroy();
                    postEditorInstance.current = null;
                }

                // Initialize Company Description Editor
                if (companyEditorRef.current) {
                    companyEditorInstance.current = new EditorJS({
                        holder: companyEditorRef.current,
                        tools: {
                            header: Header,
                            list: List,
                            paragraph: {
                                class: Paragraph,
                                inlineToolbar: true,
                            },
                            underline: Underline
                        },
                        data: {
                            blocks: htmlToEditorBlocks(formData.company_description || '')
                        },
                        placeholder: 'Décrivez l\'entreprise...',
                        minHeight: 200
                    });
                }

                // Initialize Post Description Editor
                if (postEditorRef.current) {
                    postEditorInstance.current = new EditorJS({
                        holder: postEditorRef.current,
                        tools: {
                            header: Header,
                            list: List,
                            paragraph: {
                                class: Paragraph,
                                inlineToolbar: true,
                            },
                            underline: Underline
                        },
                        data: {
                            blocks: htmlToEditorBlocks(formData.description || '')
                        },
                        placeholder: 'Décrivez le poste...',
                        minHeight: 200
                    });
                }
            }, 100);
        }

        // Cleanup on unmount
        return () => {
            if (companyEditorInstance.current) {
                companyEditorInstance.current.destroy();
                companyEditorInstance.current = null;
            }
            if (postEditorInstance.current) {
                postEditorInstance.current.destroy();
                postEditorInstance.current = null;
            }
        };
    }, [showEditModal, formData.company_description, formData.description]);

    // Debug: Log formData and cities when modal opens
    useEffect(() => {
        if (showEditModal && editingPost) {
            console.log('=== DEBUG MODAL ===');
            console.log('editingPost.city_id:', editingPost.city_id);
            console.log('formData.city_id:', formData.city_id);
            console.log('Cities loaded:', cities.length);
            console.log('Cities:', cities.map(c => ({ id: c.id, name: c.name })));
            
            // Vérifier si la ville existe dans la liste
            const cityExists = cities.find(c => c.id === editingPost.city_id);
            console.log('City exists in list:', cityExists);
        }
    }, [showEditModal, editingPost, formData.city_id, cities]);

    // Handle form input changes
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Helper function to convert EditorJS blocks to HTML
    const editorBlocksToHtml = (blocks) => {
        if (!blocks || blocks.length === 0) return '';
        
        return blocks.map(block => {
            switch (block.type) {
                case 'paragraph':
                    return `<p>${block.data.text}</p>`;
                case 'header':
                    return `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
                case 'list':
                    const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
                    const items = block.data.items.map(item => `<li>${item}</li>`).join('');
                    return `<${tag}>${items}</${tag}>`;
                default:
                    return '';
            }
        }).join('');
    };

    // Handle form submission
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            };
            
            // Get latest content from EditorJS instances
            let companyDesc = formData.company_description || '';
            let postDesc = formData.description || '';
            
            if (companyEditorInstance.current) {
                const companyData = await companyEditorInstance.current.save();
                companyDesc = editorBlocksToHtml(companyData.blocks);
            }
            
            if (postEditorInstance.current) {
                const postData = await postEditorInstance.current.save();
                postDesc = editorBlocksToHtml(postData.blocks);
            }
            
            // Prepare payload with nested company structure
            const dataToSubmit = {
                country_code: "SN",
                title: formData.title,
                description: postDesc,
                category_id: parseInt(formData.category_id) || null,
                post_type_id: parseInt(formData.post_type_id) || null,
                city_id: parseInt(formData.city_id) || null,
                contact_name: formData.contact_name,
                email: formData.email,
                phone: formData.phone,
                application_url: formData.application_url,
                salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
                salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
                salary_type: formData.salary_type,
                negotiable: formData.negotiable,
                start_date: formData.start_date,
                end_date: formData.end_date,
                tags: formData.tags,
                accept_terms: true,
                email_verified_at: editingPost.email_verified_at,
                phone_verified_at: editingPost.phone_verified_at,
                reviewed_at: editingPost.reviewed_at,
                archived_at: editingPost.archived_at,
                company: {
                    name: formData.company_name,
                    description: companyDesc
                }
            };
            
            await axiosClient.put(
                `/posts/${editingPost.id}`,
                dataToSubmit,
                config
            );
            
            swal('Succès!', 'Annonce mise à jour avec succès', 'success');
            setShowEditModal(false);
            setEditingPost(null);
            fetchPosts();
        } catch (error) {
            console.error('Error updating post:', error);
            const errorMsg = error?.response?.data?.message || 'Erreur lors de la mise à jour';
            swal('Erreur', errorMsg, 'error');
        }
    };

    // Handle delete post
    const handleDeletePost = async (postId) => {
        const confirmed = await swal({
            title: "Êtes-vous sûr?",
            text: "Voulez-vous vraiment supprimer cette annonce? Cette action est irréversible.",
            icon: "warning",
            buttons: {
                cancel: "Annuler",
                confirm: {
                    text: "Supprimer",
                    value: true,
                    className: "btn-danger"
                }
            },
            dangerMode: true,
        });

        if (!confirmed) return;

        try {
            const config = {
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            };
            
            await axiosClient.delete(
                `/admin/posts/${postId}`,
                config
            );
            
            swal('Succès!', 'Annonce supprimée avec succès', 'success');
            setExpandedPost(null);
            fetchPosts();
        } catch (error) {
            console.error('Error deleting post:', error);
            const errorMsg = error?.response?.data?.message || 'Erreur lors de la suppression';
            swal('Erreur', errorMsg, 'error');
        }
    };

    // Handle toggle with confirmation
    const handleToggle = async (postId, field, currentValue, label) => {
        const newValue = currentValue ? 0 : 1;
        const action = newValue ? 'activer' : 'désactiver';
        
        const confirmed = await swal({
            title: 'Confirmation',
            text: `Voulez-vous vraiment ${action} "${label}" ?`,
            icon: 'warning',
            buttons: ['Annuler', 'Confirmer'],
            dangerMode: true,
        });

        if (!confirmed) return;

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    ...getAuthHeader(),
                },
            };

            // Use specific API routes for Verified Email
            if (field === 'verified_email') {
                const endpoint = newValue 
                    ? `/admin/posts/${postId}/verify/email`
                    : `/admin/posts/${postId}/unverify/email`;
                
                await axiosClient.put(endpoint, {}, config);
            } else if (field === 'reviewed') {
                // Use approve API endpoint for reviewed field with approve parameter
                const endpoint = `/admin/posts/${postId}/approve`;
                const data = { approve: newValue };
                
                await axiosClient.put(endpoint, data, config);
            } else if (field === 'featured') {
                await axiosClient.put(
                    `/admin/posts/${postId}/featured`,
                    { featured: !!newValue },
                    config
                );
            } else {
                // For other fields, use PUT
                const updateData = { [field]: newValue };
                await axiosClient.put(
                    `/posts/${postId}`,
                    updateData,
                    config
                );
            }

            // Update local state - for reviewed field, update reviewed_at
            setPosts(prevPosts => 
                prevPosts.map(p => {
                    if (p.id === postId) {
                        if (field === 'reviewed') {
                            return { ...p, reviewed_at: newValue ? new Date().toISOString() : null };
                        }
                        if (field === 'featured') {
                            return { ...p, featured: newValue };
                        }
                        return { ...p, [field]: newValue };
                    }
                    return p;
                })
            );
            
            swal('Succès!', `${label} ${newValue ? 'activé' : 'désactivé'} avec succès`, 'success');
        } catch (error) {
            console.error('Error updating post:', error);
            const errorMsg = error?.response?.data?.message || 'Erreur lors de la mise à jour';
            swal('Erreur', errorMsg, 'error');
        }
    };

    // Sélectionner tous les posts
    const toggleSelectAll = () => {
        if (selectedPosts.length === posts.length) {
            setSelectedPosts([]);
        } else {
            setSelectedPosts(posts.map(p => p.id));
        }
    };

    // Approuver les posts sélectionnés
    const handleApprove = async () => {
        if (selectedPosts.length === 0) {
            swal('Attention', 'Veuillez sélectionner au moins une annonce', 'warning');
            return;
        }
        // Logique d'approbation
        swal('Succès', `${selectedPosts.length} annonce(s) approuvée(s)`, 'success');
        setSelectedPosts([]);
    };

    // Désapprouver les posts sélectionnés
    const handleDisapprove = async () => {
        if (selectedPosts.length === 0) {
            swal('Attention', 'Veuillez sélectionner au moins une annonce', 'warning');
            return;
        }
        
        try {
            const config = {
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader()
                }
            };

            // Utiliser l'endpoint approve avec approve: false pour désapprouver
            for (const postId of selectedPosts) {
                await axiosClient.put(`/admin/posts/${postId}/approve`, { approve: false }, config);
            }
            
            swal('Succès', `${selectedPosts.length} annonce(s) désapprouvée(s)`, 'success');
            setSelectedPosts([]);
            fetchPosts(); // Rafraîchir la liste
        } catch (error) {
            console.error('Error disapproving posts:', error);
            swal('Erreur', 'Erreur lors de la désapprobation des annonces', 'error');
        }
    };

    // Supprimer les posts sélectionnés
    const handleDelete = async () => {
        if (selectedPosts.length === 0) {
            swal('Attention', 'Veuillez sélectionner au moins une annonce', 'warning');
            return;
        }
        
        swal({
            title: "Êtes-vous sûr?",
            text: `Voulez-vous vraiment supprimer ${selectedPosts.length} annonce(s)?`,
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                // Logique de suppression
                swal('Succès', `${selectedPosts.length} annonce(s) supprimée(s)`, 'success');
                setSelectedPosts([]);
                fetchPosts();
            }
        });
    };

    // Formater la date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR');
    };

    // Obtenir le logo
    const getLogo = (post) => {
        if (post.logo_url) {
            return post.logo_url.small || post.logo_url.medium || post.logo_url.full;
        }
        return null;
    };

    return (
        <>
            <div className="d-flex align-items-center mb-4 flex-wrap">
                <h4 className="fs-20 font-w600 me-auto">
                    Annonces 
                    <span className="text-muted ms-2" style={{fontSize: '14px'}}>
                        Affichage des éléments 1 à {Math.min(itemsPerPage, posts.length)} sur {posts.length}
                    </span>
                </h4>
                <div className="d-flex gap-2">
                    <Link to="/admin/job-list" className="btn btn-outline-primary btn-sm">
                        Tableau de bord
                    </Link>
                    <Link to="/admin/job-list" className="btn btn-outline-primary btn-sm">
                        Annonces
                    </Link>
                    <Link to="/admin/job-list" className="btn btn-primary btn-sm">
                        Liste
                    </Link>
                </div>
            </div>

            {/* Boutons d'action - Affichés uniquement si des posts sont sélectionnés */}
            {selectedPosts.length > 0 && (
                <div className="mb-3 d-flex gap-2 align-items-center" style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px',
                    border: '1px solid #dee2e6'
                }}>
                    <span className="me-3" style={{ fontWeight: '500', color: '#495057' }}>
                        {selectedPosts.length} sélectionné{selectedPosts.length > 1 ? 's' : ''}
                    </span>
                    <button 
                        className="btn btn-success btn-sm"
                        onClick={handleApprove}
                        style={{ borderRadius: '6px' }}
                    >
                        <i className="fas fa-check me-2"></i>Approve
                    </button>
                    <button 
                        className="btn btn-secondary btn-sm"
                        onClick={handleDisapprove}
                        style={{ borderRadius: '6px' }}
                    >
                        <i className="fas fa-times me-2"></i>Disapprove
                    </button>
                    <button 
                        className="btn btn-danger btn-sm"
                        onClick={handleDelete}
                        style={{ borderRadius: '6px' }}
                    >
                        <i className="fas fa-trash me-2"></i>Delete
                    </button>
                </div>
            )}

            {/* Filtres */}
            <div className="card mb-3">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-2">
                            <input 
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Recherche (titre)"
                                value={filters.search}
                                onChange={(e) => setFilters({...filters, search: e.target.value})}
                            />
                        </div>
                        <div className="col-md-2">
                            <input 
                                type="date"
                                className="form-control form-control-sm"
                                placeholder="Date début"
                                value={filters.from_date}
                                onChange={(e) => setFilters({...filters, from_date: e.target.value})}
                            />
                        </div>
                        <div className="col-md-2">
                            <input 
                                type="date"
                                className="form-control form-control-sm"
                                placeholder="Date fin"
                                value={filters.to_date}
                                onChange={(e) => setFilters({...filters, to_date: e.target.value})}
                            />
                        </div>
                        <div className="col-md-2">
                            <select 
                                className="form-control form-control-sm"
                                value={filters.country_code}
                                onChange={(e) => setFilters({...filters, country_code: e.target.value})}
                            >
                                <option value="">Tous les pays</option>
                                <option value="SN">Sénégal</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select 
                                className="form-control form-control-sm"
                                value={filters.status}
                                onChange={(e) => setFilters({...filters, status: e.target.value})}
                            >
                                <option value="">Tous les statuts</option>
                                <option value="1">Approuvé</option>
                                <option value="2">Non approuvé</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select 
                                className="form-control form-control-sm"
                                value={filters.hasPromotion}
                                onChange={(e) => setFilters({...filters, hasPromotion: e.target.value})}
                            >
                                <option value="">Promotion</option>
                                <option value="pending">En attente</option>
                                <option value="onHold">Approuvée (pas active)</option>
                                <option value="valid">Active/Valide</option>
                                <option value="expired">Expirée</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select 
                                className="form-control form-control-sm"
                                value={filters.hasValidPromotion}
                                onChange={(e) => setFilters({...filters, hasValidPromotion: e.target.value})}
                            >
                                <option value="">Type promotion</option>
                                <option value="real">Payée</option>
                                <option value="fake">Manuelle (admin)</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sélecteur d'éléments par page */}
            <div className="mb-3">
                <select 
                    className="form-control form-control-sm" 
                    style={{width: '100px', display: 'inline-block'}}
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>
                <span className="ms-2">enregistrement par page</span>
            </div>

            {/* Table */}
            <div className="row">
                <div className="col-xl-12">
                    <div className="table-responsive">
                        <table className="table table-hover mb-4">
                            <thead className="table-light">
                                <tr>
                                    <th style={{width: '40px'}}>
                                        <input 
                                            type="checkbox"
                                            checked={selectedPosts.length === posts.length && posts.length > 0}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th>Date</th>
                                    <th>Title</th>
                                    <th>Logo</th>
                                    <th>Company Name</th>
                                    <th>Ville</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Chargement...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : posts.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4">
                                            Aucune annonce trouvée
                                        </td>
                                    </tr>
                                ) : (
                                    posts.map((post) => (
                                        <React.Fragment key={post.id}>
                                            <tr style={{ backgroundColor: expandedPost === post.id ? '#f8f9fa' : 'transparent' }}>
                                                <td>
                                                    <input 
                                                        type="checkbox"
                                                        checked={selectedPosts.includes(post.id)}
                                                        onChange={() => toggleSelectPost(post.id)}
                                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                    />
                                                </td>
                                                <td>{formatDate(post.created_at)}</td>
                                                <td>
                                                    <Link 
                                                        to={`/job-detail?id=${post.id}`}
                                                        style={{ color: '#00d4bd', fontWeight: '500', textDecoration: 'none' }}
                                                    >
                                                        {post.title}
                                                    </Link>
                                                </td>
                                                <td>
                                                    {getLogo(post) ? (
                                                        <img 
                                                            src={getLogo(post)} 
                                                            alt={post.company_name}
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                objectFit: 'contain',
                                                                borderRadius: '4px'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div 
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                                                borderRadius: '4px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {post.company_name?.charAt(0) || 'C'}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong style={{ fontSize: '14px' }}>{post.company_name || '-'}</strong>
                                                        {post.user && (
                                                            <div className="text-muted small">
                                                                By: {post.user.name || post.user_id}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span style={{ fontSize: '20px' }}>🇸🇳</span> {post.city || 'Sénégal'}
                                                </td>
                                            </tr>
                                            {expandedPost === post.id && (
                                                <tr>
                                                    <td colSpan="6" style={{ padding: '0', backgroundColor: 'white', borderTop: 'none' }}>
                                                        <div style={{ 
                                                            padding: '25px 30px', 
                                                            background: 'white',
                                                            maxWidth: '420px',
                                                            margin: '0 auto',
                                                            textAlign: 'center'
                                                        }}>
                                                            {/* Company Name */}
                                                            <div style={{ 
                                                                fontSize: '18px', 
                                                                fontWeight: '900', 
                                                                color: '#000',
                                                                marginBottom: '12px',
                                                                lineHeight: '1.3',
                                                                letterSpacing: '-0.02em'
                                                            }}>
                                                                <span style={{ fontWeight: '900' }}>Company Name</span>{' '}
                                                                <span style={{ fontWeight: '400' }}>{post.company_name || 'Dev Senegal'}</span>
                                                            </div>

                                                            {/* By: User */}
                                                            <div style={{ 
                                                                fontSize: '15px',
                                                                marginBottom: '18px',
                                                                paddingBottom: '18px',
                                                                borderBottom: '1px solid #e5e7eb'
                                                            }}>
                                                                <span style={{ fontWeight: '400', color: '#000' }}>By: </span>
                                                                {loadingUser ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                ) : (
                                                                    <Link 
                                                                        to={`/admin/users/${post.user_id}`}
                                                                        style={{ 
                                                                            color: '#3b82f6', 
                                                                            textDecoration: 'none',
                                                                            fontWeight: '700',
                                                                            transition: 'color 0.2s',
                                                                            letterSpacing: '-0.01em'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                                                                        onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
                                                                    >
                                                                        {expandedPostUser?.name || post.user?.name || 'Babacar GUEYE'}
                                                                    </Link>
                                                                )}
                                                            </div>

                                                            {/* City */}
                                                            <div style={{ 
                                                                fontSize: '18px',
                                                                fontWeight: '900',
                                                                marginBottom: '18px',
                                                                paddingBottom: '18px',
                                                                borderBottom: '1px solid #e5e7eb',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '10px',
                                                                letterSpacing: '-0.02em'
                                                            }}>
                                                                <span style={{ color: '#000' }}>City</span>
                                                                <span style={{ fontSize: '22px' }}>🇸🇳</span>
                                                                <span style={{ 
                                                                    color: '#3b82f6',
                                                                    fontWeight: '400'
                                                                }}>
                                                                    - {post.city || 'Kaolack'}
                                                                </span>
                                                            </div>

                                                            {/* Verified Email Toggle */}
                                                            <div style={{ 
                                                                fontSize: '16px',
                                                                fontWeight: '900',
                                                                marginBottom: '15px',
                                                                paddingBottom: '15px',
                                                                borderBottom: '1px solid #e5e7eb',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                letterSpacing: '-0.01em'
                                                            }}>
                                                                <span style={{ color: '#000' }}>Verified Email</span>
                                                                <label style={{
                                                                    position: 'relative',
                                                                    display: 'inline-block',
                                                                    width: '52px',
                                                                    height: '28px',
                                                                    cursor: 'pointer'
                                                                }}>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={post.verified_email || post.email_verified_at ? true : false}
                                                                        onChange={() => handleToggle(
                                                                            post.id, 
                                                                            'verified_email', 
                                                                            post.verified_email || post.email_verified_at ? 1 : 0,
                                                                            'Verified Email'
                                                                        )}
                                                                        style={{ opacity: 0, width: 0, height: 0 }}
                                                                    />
                                                                    <span style={{
                                                                        position: 'absolute',
                                                                        cursor: 'pointer',
                                                                        top: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                        bottom: 0,
                                                                        backgroundColor: (post.verified_email || post.email_verified_at) ? '#3b82f6' : '#cbd5e1',
                                                                        transition: '0.3s',
                                                                        borderRadius: '34px',
                                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                                                    }}>
                                                                        <span style={{
                                                                            position: 'absolute',
                                                                            content: '',
                                                                            height: '22px',
                                                                            width: '22px',
                                                                            left: (post.verified_email || post.email_verified_at) ? '27px' : '3px',
                                                                            bottom: '3px',
                                                                            backgroundColor: 'white',
                                                                            transition: '0.3s',
                                                                            borderRadius: '50%',
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                        }}></span>
                                                                    </span>
                                                                </label>
                                                            </div>

                                                            {/* Reviewed Toggle */}
                                                            <div style={{ 
                                                                fontSize: '16px',
                                                                fontWeight: '900',
                                                                marginBottom: '18px',
                                                                paddingBottom: '18px',
                                                                borderBottom: '1px solid #e5e7eb',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                letterSpacing: '-0.01em'
                                                            }}>
                                                                <span style={{ color: '#000' }}>Reviewed</span>
                                                                <label style={{
                                                                    position: 'relative',
                                                                    display: 'inline-block',
                                                                    width: '52px',
                                                                    height: '28px',
                                                                    cursor: 'pointer'
                                                                }}>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={post.reviewed_at ? true : false}
                                                                        onChange={() => handleToggle(
                                                                            post.id, 
                                                                            'reviewed', 
                                                                            post.reviewed_at ? 1 : 0,
                                                                            'Reviewed'
                                                                        )}
                                                                        style={{ opacity: 0, width: 0, height: 0 }}
                                                                    />
                                                                    <span style={{
                                                                        position: 'absolute',
                                                                        cursor: 'pointer',
                                                                        top: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                        bottom: 0,
                                                                        backgroundColor: post.reviewed_at ? '#3b82f6' : '#cbd5e1',
                                                                        transition: '0.3s',
                                                                        borderRadius: '34px',
                                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                                                    }}>
                                                                        <span style={{
                                                                            position: 'absolute',
                                                                            content: '',
                                                                            height: '22px',
                                                                            width: '22px',
                                                                            left: post.reviewed_at ? '27px' : '3px',
                                                                            bottom: '3px',
                                                                            backgroundColor: 'white',
                                                                            transition: '0.3s',
                                                                            borderRadius: '50%',
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                        }}></span>
                                                                    </span>
                                                                </label>
                                                            </div>

                                                            {/* Featured Toggle */}
                                                            <div style={{ 
                                                                fontSize: '16px',
                                                                fontWeight: '900',
                                                                marginBottom: '18px',
                                                                paddingBottom: '18px',
                                                                borderBottom: '1px solid #e5e7eb',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                letterSpacing: '-0.01em'
                                                            }}>
                                                                <span style={{ color: '#000' }}>En vedette</span>
                                                                <label style={{
                                                                    position: 'relative',
                                                                    display: 'inline-block',
                                                                    width: '52px',
                                                                    height: '28px',
                                                                    cursor: 'pointer'
                                                                }}>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={post.featured ? true : false}
                                                                        onChange={() => handleToggle(
                                                                            post.id,
                                                                            'featured',
                                                                            post.featured ? 1 : 0,
                                                                            'En vedette'
                                                                        )}
                                                                        style={{ opacity: 0, width: 0, height: 0 }}
                                                                    />
                                                                    <span style={{
                                                                        position: 'absolute',
                                                                        cursor: 'pointer',
                                                                        top: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                        bottom: 0,
                                                                        backgroundColor: post.featured ? '#3b82f6' : '#cbd5e1',
                                                                        transition: '0.3s',
                                                                        borderRadius: '34px',
                                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                                                    }}>
                                                                        <span style={{
                                                                            position: 'absolute',
                                                                            content: '',
                                                                            height: '22px',
                                                                            width: '22px',
                                                                            left: post.featured ? '27px' : '3px',
                                                                            bottom: '3px',
                                                                            backgroundColor: 'white',
                                                                            transition: '0.3s',
                                                                            borderRadius: '50%',
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                        }}></span>
                                                                    </span>
                                                                </label>
                                                            </div>

                                                            {/* Actions */}
                                                            <div style={{ 
                                                                fontSize: '16px',
                                                                fontWeight: '900',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                letterSpacing: '-0.01em'
                                                            }}>
                                                                <span style={{ color: '#000' }}>Actions</span>
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    <button 
                                                                        onClick={() => fetchPostDetails(post.id)}
                                                                        style={{ 
                                                                            borderRadius: '6px',
                                                                            backgroundColor: '#8b5cf6',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            padding: '8px 16px',
                                                                            fontWeight: '700',
                                                                            fontSize: '13px',
                                                                            transition: 'all 0.2s',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            cursor: 'pointer',
                                                                            letterSpacing: '-0.01em'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.target.style.backgroundColor = '#7c3aed';
                                                                            e.target.style.transform = 'translateY(-1px)';
                                                                            e.target.style.boxShadow = '0 3px 10px rgba(139, 92, 246, 0.3)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.target.style.backgroundColor = '#8b5cf6';
                                                                            e.target.style.transform = 'translateY(0)';
                                                                            e.target.style.boxShadow = 'none';
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-pen-to-square"></i>
                                                                        Edit
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => handleDeletePost(post.id)}
                                                                        style={{ 
                                                                            borderRadius: '6px',
                                                                            backgroundColor: '#ef4444',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            padding: '8px 16px',
                                                                            fontWeight: '700',
                                                                            fontSize: '13px',
                                                                            transition: 'all 0.2s',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            cursor: 'pointer',
                                                                            letterSpacing: '-0.01em'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.target.style.backgroundColor = '#dc2626';
                                                                            e.target.style.transform = 'translateY(-1px)';
                                                                            e.target.style.boxShadow = '0 3px 10px rgba(239, 68, 68, 0.3)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.target.style.backgroundColor = '#ef4444';
                                                                            e.target.style.transform = 'translateY(0)';
                                                                            e.target.style.boxShadow = 'none';
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-trash"></i>
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="d-md-flex d-block align-items-center justify-content-between text-center flex-wrap mt-md-0 mt-3">
                    <div className='mb-md-0 mb-2'>
                        <h5 className="mb-0">
                            Affichage de {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, posts.length)} sur {posts.length} entrées
                        </h5>
                    </div>
                    <nav>
                        <ul className="pagination pagination-circle justify-content-center">
                            <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link"
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <i className='fa fa-angle-double-left' />
                                </button>
                            </li>
{totalPages > 0 && (() => {
                                const maxVisible = 10;
                                const halfVisible = Math.floor(maxVisible / 2);
                                let startPage = Math.max(1, currentPage - halfVisible);
                                let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                                
                                if (endPage - startPage < maxVisible - 1) {
                                    startPage = Math.max(1, endPage - maxVisible + 1);
                                }
                                
                                const pages = [];
                                
                                if (startPage > 1) {
                                    pages.push(
                                        <li key="page-1" className="page-item">
                                            <button className="page-link" onClick={() => setCurrentPage(1)}>1</button>
                                        </li>
                                    );
                                    if (startPage > 2) {
                                        pages.push(
                                            <li key="ellipsis-start" className="page-item disabled">
                                                <span className="page-link">...</span>
                                            </li>
                                        );
                                    }
                                }
                                
                                for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                        <li 
                                            key={`page-${i}`}
                                            className={`page-item ${currentPage === i ? 'active' : ''}`}
                                        >
                                            <button 
                                                className="page-link"
                                                onClick={() => setCurrentPage(i)}
                                            >
                                                {i}
                                            </button>
                                        </li>
                                    );
                                }
                                
                                if (endPage < totalPages) {
                                    if (endPage < totalPages - 1) {
                                        pages.push(
                                            <li key="ellipsis-end" className="page-item disabled">
                                                <span className="page-link">...</span>
                                            </li>
                                        );
                                    }
                                    pages.push(
                                        <li key={`page-${totalPages}`} className="page-item">
                                            <button className="page-link" onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                                        </li>
                                    );
                                }
                                
                                return pages;
                            })()}
                            <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link"
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    <i className='fa fa-angle-double-right' />
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
                    <div className="modal-dialog" style={{ maxWidth: '900px', margin: '1.75rem auto' }}>
                        <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                            <div className="modal-header" style={{ borderBottom: '1px solid #e5e7eb', padding: '20px 30px', backgroundColor: '#f8f9fa', borderRadius: '12px 12px 0 0' }}>
                                <h4 className="modal-title" style={{ fontWeight: '700', fontSize: '20px', color: '#1f2937' }}>
                                    <i className="fas fa-edit me-2" style={{ color: '#8b5cf6' }}></i>
                                    Modifier l'annonce
                                </h4>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditingPost(null);
                                    }}
                                    style={{ fontSize: '14px' }}
                                ></button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', padding: '30px' }}>
                                    <div className="row">
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Titre de l'annonce<span className="text-danger scale5 ms-2">*</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                className="form-control solid" 
                                                name="title"
                                                value={formData.title || ''}
                                                onChange={handleFormChange}
                                                placeholder="Titre"
                                                required
                                            />
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Nom de l'entreprise<span className="text-danger scale5 ms-2">*</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                className="form-control solid" 
                                                name="company_name"
                                                value={formData.company_name || ''}
                                                onChange={handleFormChange}
                                                placeholder="Nom de l'entreprise"
                                                required
                                            />
                                        </div>
                                        <div className="col-xl-12 mb-4">
                                            <label className="form-label font-w600">
                                                Description de l'entreprise<span className="text-danger scale5 ms-2">*</span>
                                            </label>
                                            <div 
                                                ref={companyEditorRef}
                                                style={{ 
                                                    border: '1px solid #d1d5db', 
                                                    borderRadius: '6px',
                                                    minHeight: '200px',
                                                    backgroundColor: 'white'
                                                }}
                                            />
                                        </div>
                                        <div className="col-xl-12 mb-4">
                                            <label className="form-label font-w600">
                                                Description de l'offre<span className="text-danger scale5 ms-2">*</span>
                                            </label>
                                            <div 
                                                ref={postEditorRef}
                                                style={{ 
                                                    border: '1px solid #d1d5db', 
                                                    borderRadius: '6px',
                                                    minHeight: '200px',
                                                    backgroundColor: 'white'
                                                }}
                                            />
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Ville<span className="text-danger scale5 ms-2">*</span>
                                            </label>
                                            <select 
                                                className="form-control solid" 
                                                name="city_id"
                                                value={formData.city_id ? String(formData.city_id) : ''}
                                                onChange={handleFormChange}
                                                required
                                                disabled={loadingCities}
                                            >
                                                <option value="">Sélectionner une ville...</option>
                                                {cities.map((city) => (
                                                    <option key={city.id} value={String(city.id)}>
                                                        {city.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingCities && <small className="text-muted">Chargement des villes...</small>}
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Catégorie
                                            </label>
                                            <select 
                                                className="form-control solid" 
                                                name="category_id"
                                                value={formData.category_id ? String(formData.category_id) : ''}
                                                onChange={handleFormChange}
                                                disabled={loadingCategories}
                                            >
                                                <option value="">Sélectionner une catégorie...</option>
                                                {categories.map((category) => (
                                                    <option key={category.id} value={String(category.id)}>
                                                        {category.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingCategories && <small className="text-muted">Chargement des catégories...</small>}
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Type de poste
                                            </label>
                                            <select 
                                                className="form-control solid" 
                                                name="post_type_id"
                                                value={formData.post_type_id ? String(formData.post_type_id) : ''}
                                                onChange={handleFormChange}
                                                disabled={loadingPostTypes}
                                            >
                                                <option value="">Sélectionner un type...</option>
                                                {postTypes.map((postType) => (
                                                    <option key={postType.id} value={String(postType.id)}>
                                                        {postType.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingPostTypes && <small className="text-muted">Chargement des types...</small>}
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Email de contact
                                            </label>
                                            <input 
                                                type="email" 
                                                className="form-control solid" 
                                                name="email"
                                                value={formData.email || ''}
                                                onChange={handleFormChange}
                                                placeholder="contact@example.com"
                                            />
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Téléphone
                                            </label>
                                            <input 
                                                type="tel" 
                                                className="form-control solid" 
                                                name="phone"
                                                value={formData.phone || ''}
                                                onChange={handleFormChange}
                                                placeholder="+221 XX XXX XX XX"
                                            />
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Nom du contact
                                            </label>
                                            <input 
                                                type="text" 
                                                className="form-control solid" 
                                                name="contact_name"
                                                value={formData.contact_name || ''}
                                                onChange={handleFormChange}
                                                placeholder="Nom du contact"
                                            />
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                URL de candidature
                                            </label>
                                            <input 
                                                type="url" 
                                                className="form-control solid" 
                                                name="application_url"
                                                value={formData.application_url || ''}
                                                onChange={handleFormChange}
                                                placeholder="https://..."
                                            />
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Date de début
                                            </label>
                                            <input 
                                                type="date" 
                                                className="form-control solid" 
                                                name="start_date"
                                                value={formData.start_date || ''}
                                                onChange={handleFormChange}
                                            />
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Date de fin
                                            </label>
                                            <input 
                                                type="date" 
                                                className="form-control solid" 
                                                name="end_date"
                                                value={formData.end_date || ''}
                                                onChange={handleFormChange}
                                            />
                                        </div>
                                        <div className="col-xl-4 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Salaire minimum
                                            </label>
                                            <input 
                                                type="number" 
                                                className="form-control solid" 
                                                name="salary_min"
                                                value={formData.salary_min || ''}
                                                onChange={handleFormChange}
                                                placeholder="Salaire minimum"
                                            />
                                        </div>
                                        <div className="col-xl-4 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Salaire maximum
                                            </label>
                                            <input 
                                                type="number" 
                                                className="form-control solid" 
                                                name="salary_max"
                                                value={formData.salary_max || ''}
                                                onChange={handleFormChange}
                                                placeholder="Salaire maximum"
                                            />
                                        </div>
                                        <div className="col-xl-4 col-md-6 mb-4">
                                            <label className="form-label font-w600">
                                                Type de salaire
                                            </label>
                                            <select 
                                                className="form-control solid"
                                                name="salary_type"
                                                value={formData.salary_type || ''}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Sélectionner...</option>
                                                <option value="monthly">Mensuel</option>
                                                <option value="yearly">Annuel</option>
                                                <option value="hourly">Horaire</option>
                                                <option value="daily">Journalier</option>
                                            </select>
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <div className="form-check" style={{ paddingTop: '30px' }}>
                                                <input 
                                                    type="checkbox" 
                                                    className="form-check-input" 
                                                    id="negotiable"
                                                    name="negotiable"
                                                    checked={formData.negotiable === 1}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, negotiable: e.target.checked ? 1 : 0 }))}
                                                />
                                                <label className="form-check-label" htmlFor="negotiable">
                                                    Salaire négociable
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <div className="form-check" style={{ paddingTop: '30px' }}>
                                                <input 
                                                    type="checkbox" 
                                                    className="form-check-input" 
                                                    id="permanent"
                                                    name="permanent"
                                                    checked={formData.permanent === 1}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, permanent: e.target.checked ? 1 : 0 }))}
                                                />
                                                <label className="form-check-label" htmlFor="permanent">
                                                    Poste permanent
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-xl-12 mb-4">
                                            <label className="form-label font-w600">
                                                Tags (séparés par des virgules)
                                            </label>
                                            <input 
                                                type="text" 
                                                className="form-control solid" 
                                                name="tags"
                                                value={formData.tags || ''}
                                                onChange={handleFormChange}
                                                placeholder="développeur, web, react"
                                            />
                                        </div>

                                        {/* Status Toggles Section */}
                                        <div className="col-xl-12 mb-3">
                                            <hr style={{ margin: '20px 0', borderTop: '2px solid #e5e7eb' }} />
                                            <h5 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
                                                <i className="fas fa-toggle-on me-2" style={{ color: '#8b5cf6' }}></i>
                                                Statuts et vérifications
                                            </h5>
                                        </div>

                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <div className="form-check form-switch" style={{ paddingLeft: '2.5em' }}>
                                                <input 
                                                    type="checkbox" 
                                                    className="form-check-input" 
                                                    id="email_verified"
                                                    checked={editingPost?.email_verified_at ? true : false}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setEditingPost(prev => ({
                                                            ...prev,
                                                            email_verified_at: newValue ? new Date().toISOString() : null
                                                        }));
                                                    }}
                                                    style={{ 
                                                        width: '3em', 
                                                        height: '1.5em',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <label className="form-check-label" htmlFor="email_verified" style={{ fontWeight: '600', fontSize: '15px', marginLeft: '10px' }}>
                                                    <i className="fas fa-envelope-circle-check me-2" style={{ color: '#3b82f6' }}></i>
                                                    Email vérifié
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <div className="form-check form-switch" style={{ paddingLeft: '2.5em' }}>
                                                <input 
                                                    type="checkbox" 
                                                    className="form-check-input" 
                                                    id="phone_verified"
                                                    checked={editingPost?.phone_verified_at ? true : false}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setEditingPost(prev => ({
                                                            ...prev,
                                                            phone_verified_at: newValue ? new Date().toISOString() : null
                                                        }));
                                                    }}
                                                    style={{ 
                                                        width: '3em', 
                                                        height: '1.5em',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <label className="form-check-label" htmlFor="phone_verified" style={{ fontWeight: '600', fontSize: '15px', marginLeft: '10px' }}>
                                                    <i className="fas fa-phone-circle-check me-2" style={{ color: '#3b82f6' }}></i>
                                                    Téléphone vérifié
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <div className="form-check form-switch" style={{ paddingLeft: '2.5em' }}>
                                                <input 
                                                    type="checkbox" 
                                                    className="form-check-input" 
                                                    id="reviewed"
                                                    checked={editingPost?.reviewed_at ? true : false}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setEditingPost(prev => ({
                                                            ...prev,
                                                            reviewed_at: newValue ? new Date().toISOString() : null
                                                        }));
                                                    }}
                                                    style={{ 
                                                        width: '3em', 
                                                        height: '1.5em',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <label className="form-check-label" htmlFor="reviewed" style={{ fontWeight: '600', fontSize: '15px', marginLeft: '10px' }}>
                                                    <i className="fas fa-check-circle me-2" style={{ color: '#10b981' }}></i>
                                                    Reviewed
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-xl-6 col-md-6 mb-4">
                                            <div className="form-check form-switch" style={{ paddingLeft: '2.5em' }}>
                                                <input 
                                                    type="checkbox" 
                                                    className="form-check-input" 
                                                    id="archived"
                                                    checked={editingPost?.archived_at ? true : false}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setEditingPost(prev => ({
                                                            ...prev,
                                                            archived_at: newValue ? new Date().toISOString() : null
                                                        }));
                                                    }}
                                                    style={{ 
                                                        width: '3em', 
                                                        height: '1.5em',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <label className="form-check-label" htmlFor="archived" style={{ fontWeight: '600', fontSize: '15px', marginLeft: '10px' }}>
                                                    <i className="fas fa-archive me-2" style={{ color: '#6b7280' }}></i>
                                                    Archived
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '20px 30px', backgroundColor: '#f8f9fa', borderRadius: '0 0 12px 12px' }}>
                                    <button 
                                        type="button" 
                                        className="btn btn-danger light"
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setEditingPost(null);
                                        }}
                                        style={{ padding: '10px 24px', fontWeight: '600', borderRadius: '8px' }}
                                    >
                                        <i className="fas fa-times me-2"></i>Annuler
                                    </button>
                                    <button type="submit" className="btn btn-primary" style={{ padding: '10px 24px', fontWeight: '600', borderRadius: '8px', backgroundColor: '#8b5cf6', border: 'none' }}>
                                        <i className="fas fa-check me-2"></i>Enregistrer les modifications
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ModernJobLists;
